import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CLINIC_GLP1_WORKFLOW_STAGES, INTAKE_PHASES } from '../data/clinic-glp1-workflow'
import { NIMBUS_PAYLOAD_STAGES } from '../data/nimbus-payload-map'
import { generateGuidance } from '../lib/guidance-engine'
import { buildNimbusQuestion } from '../lib/nimbus-payload-builder'
import { calculateBMI, determineTrack } from '../lib/routing-engine'
import { calculateProgress } from '../lib/template-utils'
import type { AnswerMap, AnswerValue, PhaseInfo, WorkflowStage } from '../types/workflow'

interface UseClinicWorkflowReturn {
  currentStage: WorkflowStage | null
  visibleStages: WorkflowStage[]
  currentStageIndex: number
  totalStages: number
  progress: number
  currentPhase: PhaseInfo | null
  answers: AnswerMap
  completedAtByStage: Record<string, string>
  computedData: Record<string, unknown>
  guidanceData: ReturnType<typeof generateGuidance>
  isLoading: boolean
  isFirstStage: boolean
  isLastStage: boolean
  getAnswer: (field: string) => AnswerValue
  setAnswer: (field: string, value: AnswerValue) => void
  setMultipleAnswers: (newAnswers: Record<string, AnswerValue>) => void
  onNext: () => Promise<{ completed: boolean }>
  onBack: () => void
  goToStage: (index: number) => void
  validateCurrentStage: () => boolean
  setIsLoading: (loading: boolean) => void
  getSubmissionPayload: () => { stages: unknown[] }
}

export function useClinicWorkflow(
  initialAnswers?: AnswerMap,
  initialStageId?: string,
  initialCompletedAtByStage?: Record<string, string>
): UseClinicWorkflowReturn {
  const [currentStageIndex, setCurrentStageIndex] = useState(0)
  const [answers, setAnswers] = useState<AnswerMap>(initialAnswers || {})
  const answersRef = useRef<AnswerMap>(answers)
  answersRef.current = answers
  const [isLoading, setIsLoading] = useState(false)
  const [completedAtByStage, setCompletedAtByStage] = useState<Record<string, string>>(initialCompletedAtByStage || {})
  const hasRestoredStageRef = useRef(false)

  const computedData = useMemo(() => {
    const bmiData = answers.bmi_data as { height_feet?: number; height_inches?: number; weight_lbs?: number; calculated_bmi?: number } | undefined
    const bmi =
      bmiData?.calculated_bmi ||
      (bmiData?.height_feet && bmiData?.weight_lbs ? calculateBMI(bmiData.height_feet, bmiData.height_inches || 0, bmiData.weight_lbs) : undefined)
    const track = determineTrack({
      height_feet: bmiData?.height_feet,
      height_inches: bmiData?.height_inches,
      weight_lbs: bmiData?.weight_lbs,
      glp1_experience: answers.glp1_experience as string,
      has_comorbidities: answers.has_comorbidities as boolean | undefined,
      weight_related_conditions: answers.weight_related_conditions as string[] | undefined,
    })
    return {
      bmi,
      recommended_track: track.track,
      show_comorbidity_screen: bmi !== undefined && bmi >= 27 && bmi < 30,
    }
  }, [answers])

  const guidanceData = useMemo(
    () =>
      generateGuidance({
        calculatedBmi: computedData.bmi as number | undefined,
        glp1Experience: answers.glp1_experience as string | undefined,
        currentGlp1Med: answers.current_medication as string | undefined,
        hasComorbidities: answers.has_comorbidities as boolean | undefined,
      }),
    [answers, computedData.bmi]
  )

  const evaluateCondition = useCallback(
    (source: AnswerMap | Record<string, unknown>, condition: { field: string; operator: string; value: unknown }) => {
      const actualValue = source[condition.field]
      const expectedValue = condition.value

      switch (condition.operator) {
        case 'equals':
          return actualValue === expectedValue
        case 'not_equals':
          return actualValue !== expectedValue
        case 'in':
          return Array.isArray(expectedValue) ? expectedValue.includes(actualValue as never) : false
        case 'not_in':
          return Array.isArray(expectedValue) ? !expectedValue.includes(actualValue as never) : true
        case 'contains':
          return Array.isArray(actualValue) ? actualValue.includes(expectedValue as never) : false
        case 'not_contains':
          return Array.isArray(actualValue) ? !actualValue.includes(expectedValue as never) : true
        case 'exists':
          return actualValue !== undefined && actualValue !== null && actualValue !== ''
        case 'not_exists':
          return actualValue === undefined || actualValue === null || actualValue === ''
        default:
          return actualValue === expectedValue
      }
    },
    []
  )

  const visibleStages = useMemo(() => {
    return CLINIC_GLP1_WORKFLOW_STAGES.filter((stage) => {
      if (!stage.conditions?.length) return true
      return stage.conditions.every((condition) => {
        if ('field' in condition && condition.type === 'answer') {
          return evaluateCondition(answers, condition)
        }
        if ('field' in condition && condition.type === 'computed') {
          return evaluateCondition(computedData, condition)
        }
        return true
      })
    })
  }, [answers, computedData, evaluateCondition])

  useEffect(() => {
    if (hasRestoredStageRef.current) return
    if (!initialStageId || visibleStages.length === 0) return
    const restoredIndex = visibleStages.findIndex((stage) => stage.id === initialStageId)
    if (restoredIndex >= 0) {
      setCurrentStageIndex(restoredIndex)
    }
    hasRestoredStageRef.current = true
  }, [initialStageId, visibleStages])

  useEffect(() => {
    if (!visibleStages.length) return
    if (currentStageIndex >= visibleStages.length) {
      setCurrentStageIndex(visibleStages.length - 1)
    }
  }, [currentStageIndex, visibleStages.length])

  const currentStage = visibleStages[currentStageIndex] || null
  const totalStages = visibleStages.length
  const currentPhase = currentStage ? INTAKE_PHASES[currentStage.phase || 1] || null : null
  const progress = calculateProgress(currentStageIndex, totalStages)

  const getAnswer = useCallback((field: string) => answers[field], [answers])
  const setAnswer = useCallback((field: string, value: AnswerValue) => {
    setAnswers((prev) => {
      const next = { ...prev, [field]: value }
      answersRef.current = next
      return next
    })
  }, [])
  const setMultipleAnswers = useCallback(
    (newAnswers: Record<string, AnswerValue>) => setAnswers((prev) => ({ ...prev, ...newAnswers })),
    []
  )

  const onNext = useCallback(async () => {
    if (currentStage) {
      setCompletedAtByStage((prev) => ({ ...prev, [currentStage.id]: new Date().toISOString() }))
    }
    if (currentStageIndex < visibleStages.length - 1) {
      setCurrentStageIndex((prev) => prev + 1)
      return { completed: false }
    }
    return { completed: true }
  }, [currentStage, currentStageIndex, visibleStages.length])

  const onBack = useCallback(() => {
    if (currentStageIndex > 0) setCurrentStageIndex((prev) => prev - 1)
  }, [currentStageIndex])

  const goToStage = useCallback(
    (index: number) => {
      if (index < 0 || index >= visibleStages.length) return
      setCurrentStageIndex(index)
    },
    [visibleStages.length]
  )

  const validateCurrentStage = useCallback(() => {
    if (!currentStage) return false
    if (!currentStage.answerMapping?.field) return true
    const value = answers[currentStage.answerMapping.field]

    // address_form: the generic `typeof === 'object'` check accepts any
    // non-null object, which made partial addresses count as "valid". The
    // MDI readiness gate then silently rejects the order downstream (no
    // address on file). Require every shipping field — and every billing
    // field when "billing same as shipping" is off — before letting the
    // patient advance.
    if (currentStage.stageTemplate === 'address_form') {
      const v = (value || {}) as Record<string, unknown>
      const get = (k: string) => (typeof v[k] === 'string' ? (v[k] as string).trim() : '')
      const isFiveDigitZip = (z: string) => /^\d{5}(-\d{4})?$/.test(z)
      const sameAsShipping = v.billingSameAsShipping !== undefined
        ? Boolean(v.billingSameAsShipping)
        : true

      const billingOk = !!(
        get('billingAddressLine1')
        && get('billingCity')
        && get('billingState')
        && isFiveDigitZip(get('billingPostalCode'))
      )
      if (!billingOk) return false

      if (!sameAsShipping) {
        const shippingOk = !!(
          get('shippingAddressLine1')
          && get('shippingCity')
          && get('shippingState')
          && isFiveDigitZip(get('shippingPostalCode'))
        )
        if (!shippingOk) return false
      }
      return true
    }

    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'string') return value.trim().length > 0
    if (typeof value === 'object') return value !== null
    return value !== undefined && value !== null
  }, [answers, currentStage])

  const getSubmissionPayload = useCallback(() => {
    const visibleStageIndexById = new Map(visibleStages.map((stage, index) => [stage.id, index]))
    const nowIso = new Date().toISOString()

    const getCompletedAt = (sourceStageIds: string[]): string | null => {
      const candidates = sourceStageIds
        .map((stageId) => completedAtByStage[stageId])
        .filter((value): value is string => Boolean(value))

      if (!candidates.length) return null

      return candidates.sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0]
    }

    const getSourceStageIndex = (sourceStageIds: string[]): number => {
      const indices = sourceStageIds
        .map((stageId) => visibleStageIndexById.get(stageId))
        .filter((value): value is number => typeof value === 'number')

      if (!indices.length) return Number.MAX_SAFE_INTEGER
      return Math.min(...indices)
    }

    const stagedWithSourceIndex = NIMBUS_PAYLOAD_STAGES
      .map((stageDefinition) => {
        const rawQuestions = stageDefinition.buildQuestions(answers)
        const hasAnsweredQuestion = rawQuestions.some((question) => buildNimbusQuestion(question, nowIso).response.answered)
        const completedAt = getCompletedAt(stageDefinition.source_stage_ids) || (hasAnsweredQuestion ? nowIso : null)
        if (!completedAt) return null

        const questions = rawQuestions.map((question) => buildNimbusQuestion(question, completedAt))

        return {
          stage_id: stageDefinition.stage_id,
          stage_name: stageDefinition.stage_name,
          stage_type: 'questionnaire',
          source_stage_index: getSourceStageIndex(stageDefinition.source_stage_ids),
          completed_at: completedAt,
          questionnaire: {
            title: stageDefinition.questionnaire.title,
            version: stageDefinition.questionnaire.version,
            description: stageDefinition.questionnaire.description,
            questionnaire_id: stageDefinition.questionnaire.questionnaire_id,
            questions,
          },
        }
      })
      .filter((stage): stage is NonNullable<typeof stage> => Boolean(stage))
      .sort((left, right) => left.source_stage_index - right.source_stage_index)
    const stages = stagedWithSourceIndex.map((stage, index) => {
      const { source_stage_index, ...stageWithoutSourceIndex } = stage
      return {
        ...stageWithoutSourceIndex,
        stage_order: index + 1,
      }
    })

    // Include payment method data if collected during GLP-1 intake
    // Use answersRef to read the latest state (avoids stale closure after setAnswer)
    const latestAnswers = answersRef.current
    const paymentMethodData = latestAnswers.payment_method_data as
      | { paymentMethodId?: string; setupIntentId?: string }
      | undefined

    return {
      stages,
      ...(paymentMethodData?.paymentMethodId && { payment_method_data: paymentMethodData }),
    }
  }, [answers, completedAtByStage, visibleStages])

  return {
    currentStage,
    visibleStages,
    currentStageIndex,
    totalStages,
    progress,
    currentPhase,
    answers,
    completedAtByStage,
    computedData: { ...computedData, guidanceData },
    guidanceData,
    isLoading,
    isFirstStage: currentStageIndex === 0,
    isLastStage: currentStageIndex === visibleStages.length - 1,
    getAnswer,
    setAnswer,
    setMultipleAnswers,
    onNext,
    onBack,
    goToStage,
    validateCurrentStage,
    setIsLoading,
    getSubmissionPayload,
  }
}
